<template>
  <main class="container py-4 py-md-5">
    <div class="row">
      <!-- Kiri: input plat -->
      <div class="col-lg-3   mb-4">
        <div class="side">
          <h1 class="h3 mb-2">Daftar Pelanggaran ASDP</h1>
          <p class="text-muted mb-4">
            Daftar seluruh pelanggaran berdasarkan nomor TNKB dan status konfirmasi.
          </p>

          <form @submit.prevent="search">
            <label for="plat" class="form-label d-block mb-2"><strong>Nomor polisi</strong></label>
            <div class="plate">
              <input
                id="plat"
                v-model="plate"
                type="text"
                inputmode="text"
                autocomplete="off"
                autocapitalize="characters"
                spellcheck="false"
                maxlength="12"
                placeholder="B1234XYZ"
                disabled
              />
            </div>
          </form>
        </div>
      </div>

      <!-- Kanan: daftar -->
      <div class="col-lg-9" aria-live="polite">
        <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

        <div v-else-if="searched && !loading && !items.length" class="alert alert-info mb-0">
          {{ message || 'Tidak ada pelanggaran untuk nomor polisi ini.' }}
        </div>

        <template v-else-if="items.length">
          <h2 class="h5 mb-3">
            {{ total }} pelanggaran untuk <span class="plate-text">{{ queriedPlate }}</span>
          </h2>

          <div class="card">
            <ul class="list-group list-group-flush">
              <li
                v-for="(it, i) in items"
                :key="it.noref || i"
                class="list-group-item py-3 tone"
                :class="'tone--' + meta(it.status).tone"
              >
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <time class="text-muted small">Melanggar pada <u>{{ it.tanggal_pelanggaran || '-' }}</u></time>
                  <span :class="badgeClass(meta(it.status).tone)">{{ it.status }}</span>
                </div>

                <h3 class="h6 mt-2 mb-1">Pelanggaran <u>{{ pelanggaranText(it) }}</u></h3>
                <p class="text-muted small mb-0">{{ meta(it.status).hint }}</p>

                <div
                  v-if="meta(it.status).step !== null"
                  class="progress mt-2"
                  style="height: 6px;"
                  role="progressbar"
                  :aria-valuenow="meta(it.status).step"
                  aria-valuemin="0"
                  :aria-valuemax="totalSteps"
                  :aria-label="'Tahap ' + meta(it.status).step + ' dari ' + totalSteps"
                >
                  <div
                    class="progress-bar"
                    :class="{ 'bg-success': meta(it.status).tone === 'ok' }"
                    :style="{ width: progressPct(it.status) + '%' }"
                  ></div>
                </div>

                <div class="row small mt-3">
                  <!-- <div class="col-md-4 mb-2 mb-md-0">
                    <div class="text-muted">Kendaraan</div>
                    <div class="break">{{ vehicleText(it) }}</div>
                  </div> -->
                  <div class="col-md-8 mb-2 mb-md-0">
                    <div class="text-muted">Lokasi Pelanggaran:</div>
                    <div class="break">{{ locationText(it) }}</div>
                  </div>
                  <div class="col-md-3">
                    <div class="text-muted">Nomor Registrasi</div>
                    <div class="break">{{ it.noref || '-' }}</div>
                  </div>
                  <div class="col-md-1">
                    <div class="item-action">
                      <router-link target="_blank" :to="konfirmasiLink(it)" class="btn btn-primary btn-sm" title="Konfirmasi"><i class="bi bi-box-arrow-up-right"></i></router-link>
                    </div>  
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
// Sesuaikan path import ke file tempat getDaftarAsdp didefinisikan.
import { getDaftarAsdp } from './api';

const TOTAL_STEPS = 6;

// step = jumlah tahap yang sudah dilewati. null = di luar alur normal (tanpa progress bar).
const STATUS_META = {
  'Belum Divalidasi': { step: 0, tone: 'neutral', hint: 'Data pelanggaran masih menunggu pemeriksaan petugas.' },
  'Tervalidasi': { step: 1, tone: 'info', hint: 'Pelanggaran sudah diperiksa dan dinyatakan valid.' },
  'Sudah Diprint': { step: 2, tone: 'info', hint: 'Surat konfirmasi sudah dicetak dan menunggu pengiriman.' },
  'Surat Sedang Dikirim': { step: 3, tone: 'info', hint: 'Surat sedang dalam pengiriman ke alamat pemilik kendaraan.' },
  'Surat Terkirim': { step: 4, tone: 'warn', hint: 'Surat sudah terkirim. Segera lakukan konfirmasi.' },
  'Sudah Konfirmasi': { step: 5, tone: 'info', hint: 'Konfirmasi diterima. Lanjutkan ke pembayaran.' },
  'Sudah Dibayar': { step: 6, tone: 'ok', hint: 'Pelanggaran sudah dibayar. Tidak ada tindakan lanjutan.' },
  'Menyanggah': { step: null, tone: 'warn', hint: 'Sanggahan Anda sedang diproses petugas.' },
  'Terblokir': { step: null, tone: 'bad', hint: 'Batas waktu konfirmasi atau pembayaran terlewati, kendaraan diblokir.' }
};

const FALLBACK_META = { step: null, tone: 'neutral', hint: '' };

// Class ganda supaya jalan di Bootstrap 4 (badge-*) maupun 5 (bg-*).
const BADGE = {
  neutral: 'badge badge-pill rounded-pill badge-secondary bg-secondary text-white',
  info: 'badge badge-pill rounded-pill badge-primary bg-primary text-white',
  ok: 'badge badge-pill rounded-pill badge-success bg-success text-white',
  warn: 'badge badge-pill rounded-pill badge-warning bg-warning text-dark',
  bad: 'badge badge-pill rounded-pill badge-danger bg-danger text-white'
};

export default {
  name: 'DaftarKonfirmasiASDP',
  data() {
    return {
      plate: '',
      loading: false,
      searched: false,
      error: '',
      message: '',
      items: [],
      total: 0,
      queriedPlate: '',
      totalSteps: TOTAL_STEPS
    };
  },
  computed: {
    normalizedPlate() {
      return this.plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
    }
  },
  created() {
    // Tautan langsung: /daftar-konfirmasi-asdp?platnomor=B1234BCD
    const q = this.$route && this.$route.query && this.$route.query.platnomor;
    if (q) {
      this.plate = String(q);
      this.search();
    }
  },
  methods: {
    meta(status) {
      return STATUS_META[status] || FALLBACK_META;
    },
    badgeClass(tone) {
      return BADGE[tone] || BADGE.neutral;
    },
    progressPct(status) {
      const step = this.meta(status).step || 0;
      return Math.round((step / TOTAL_STEPS) * 100);
    },
    pelanggaranText(it) {
      const parts = [it.jenis, it.pelanggaran_lainnya].filter(Boolean);
      return parts.length ? parts.join(', ') : 'Jenis pelanggaran belum tersedia';
    },
    vehicleText(it) {
      const parts = [it.merk_mobil, it.tipe_mobil].filter(Boolean);
      return parts.length ? parts.join(' ') : '-';
    },
    locationText(it) {
      const parts = [it.titik_melanggar, it.wilayah].filter(Boolean);
      return parts.length ? parts.join(', ') : '-';
    },
    konfirmasiLink(it) {
      return {
        path: '/konfirmasi',
        query: { 'nomor-registrasi': it.noref, tnkb: it.plat || this.queriedPlate }
      };
    },
    async search() {
      const plat = this.normalizedPlate;
      if (!plat || this.loading) return;

      this.plate = plat;
      // Sinkronkan URL agar hasil pencarian bisa dibagikan.
      if (this.$route.query.platnomor !== plat) {
        this.$router.replace({ query: { ...this.$route.query, platnomor: plat } }).catch(() => {});
      }
      this.loading = true;
      this.searched = false;
      this.error = '';
      this.message = '';
      this.items = [];
      this.total = 0;

      try {
        const res = await getDaftarAsdp(plat);
        // Dukung respons axios ({ data: {...} }) maupun JSON langsung.
        const json = res && res.data && res.data.status !== undefined ? res.data : res;

        if (!json || !json.status) {
          this.message = (json && json.message) || '';
        } else {
          this.items = json.data || [];
          this.total = json.total || this.items.length;
          this.queriedPlate = plat;
        }
        this.searched = true;
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.error = msg || 'Data tidak dapat dimuat. Periksa koneksi Anda lalu coba lagi.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Hanya yang tidak ada di Bootstrap: pelat nomor, garis warna status, sticky. */
.plate {
  position: relative;
  background: #14171a;
  border: 3px solid #e9ecef;
  outline: 1px solid #14171a;
  border-radius: 8px;
  padding: 10px 16px 14px;
}
.plate::after {
  content: '';
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 8px;
  height: 2px;
  background: #e9ecef;
  opacity: 0.85;
}
.plate input {
  width: 100%;
  border: 0;
  background: transparent;
  color: #fff;
  text-align: center;
  font-family: 'Arial Narrow', 'Roboto Condensed', 'Helvetica Neue', Arial, sans-serif;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 4px 0 6px;
}
.plate input::placeholder {
  color: rgba(255, 255, 255, 0.28);
}
.plate input:focus {
  outline: none;
}
.plate:focus-within {
  outline: 3px solid #0d6efd;
  outline-offset: 2px;
}

.plate-text {
  font-family: 'Arial Narrow', 'Roboto Condensed', Arial, sans-serif;
  letter-spacing: 0.08em;
}

.tone {
  border-left: 4px solid #6c757d;
}
.tone--info { border-left-color: #0d6efd; }
.tone--ok { border-left-color: #198754; }
.tone--warn { border-left-color: #ffc107; }
.tone--bad { border-left-color: #dc3545; }

.break {
  overflow-wrap: anywhere;
}

@media (min-width: 992px) {
  .side {
    position: sticky;
    top: 24px;
  }
}
</style>